import React from 'react';
import {
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Checkbox,
  Typography,
  FormHelperText,
  Box,
} from '@mui/material';
import { Controller, useFormContext } from 'react-hook-form';
import type {
  AddMaterialFormValues,
  ContainerTypeOption,
} from './types';
import { getUnitsForType } from './validation';

const CONTAINER_TYPES: ContainerTypeOption[] = [
  'Tube',
  'PCR Tube',
  'Microcentrifuge Tube',
  'Falcon Tube',
  'Well Plate',
  'Flask',
  'Beaker',
  'Vial',
  'Bottle',
  'Other',
];

const WELL_PLATE_UNITS = ['µL', 'mL', 'L'] as const;

export function ContainerSpecForm() {
  const {
    control,
    watch,
    setValue,
    formState: { errors },
  } = useFormContext<AddMaterialFormValues>();

  const containerType = watch('containerType');
  const sameAsMaterial = watch('sameAsMaterial');
  const materialName = watch('name');

  const isPlate = containerType === 'Well Plate';
  const rows = watch('rows');
  const columns = watch('columns');

  const volumeUnits = getUnitsForType('Volume').filter(
    (u) => u !== 'nL',
  );

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: '170px 1fr',
        columnGap: '10px',
        alignItems: 'start',
      }}
    >
      {/* Section title */}
      <Typography
        sx={{
          fontSize: '14px',
          fontWeight: 600,
          textAlign: 'right',
          pt: '10px',
        }}
      >
        Container Specification
        <Box
          component="span"
          sx={{ color: '#dc3545', ml: '2px' }}
        >
          *
        </Box>
      </Typography>

      {/* Container fields */}
      <Box
        sx={{
          mb: '5px',
        }}
      >
        {!isPlate ? (
          <>
            {/* Type */}
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: '1fr',
                mb: '10px',
              }}
            >
              <Typography
                sx={{
                  fontSize: '14px',
                  color: '#555',
                  mb: '4px',
                }}
              >
                Container Type
                <Box
                  component="span"
                  sx={{ color: '#dc3545', ml: '2px' }}
                >
                  *
                </Box>
              </Typography>

              <Controller
                name="containerType"
                control={control}
                render={({ field }) => (
                  <FormControl
                    fullWidth
                    size="small"
                    error={!!errors.containerType}
                  >
                    <Select
                      {...field}
                      displayEmpty
                      onChange={(e) => {
                        field.onChange(e);

                        if (e.target.value === 'Well Plate') {
                          setValue('sameAsMaterial', false);
                        }
                      }}
                    >
                      {CONTAINER_TYPES.map((t) => (
                        <MenuItem key={t} value={t}>
                          {t}
                        </MenuItem>
                      ))}
                    </Select>

                    {errors.containerType && (
                      <FormHelperText>
                        {errors.containerType.message}
                      </FormHelperText>
                    )}
                  </FormControl>
                )}
              />
            </Box>

            {/* Container Name, Volume, Vendor in single line */}
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr 1fr',
                columnGap: '12px',
                mb: '10px',
              }}
            >
              {/* Container Name */}
              <Box>
                <Typography
                  sx={{
                    fontSize: '14px',
                    color: '#555',
                    mb: '4px',
                  }}
                >
                  Container Name
                  <Box
                    component="span"
                    sx={{ color: '#dc3545', ml: '2px' }}
                  >
                    *
                  </Box>
                </Typography>
                <Controller
                  name="containerName"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      size="small"
                      fullWidth
                      placeholder="Name"
                      required
                      disabled={sameAsMaterial}
                      value={
                        sameAsMaterial
                          ? materialName
                          : field.value
                      }
                      error={!!errors.containerName}
                      helperText={errors.containerName?.message}
                    />
                  )}
                />
              </Box>

              {/* Volume */}
              <Box>
                <Typography
                  sx={{
                    fontSize: '14px',
                    color: '#555',
                    mb: '4px',
                  }}
                >
                  Volume
                </Typography>

                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 70px',
                  }}
                >
                  <Controller
                    name="containerVolumeAmount"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        type="number"
                        size="small"
                        fullWidth
                        placeholder="Value"
                        error={
                          !!errors.containerVolumeAmount
                        }
                        helperText={
                          errors.containerVolumeAmount?.message
                        }
                        slotProps={{
                          htmlInput: {
                            min: 0,
                          },
                        }}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderTopRightRadius: 0,
                            borderBottomRightRadius: 0,
                          },
                        }}
                      />
                    )}
                  />

                  <Controller
                    name="containerVolumeUnits"
                    control={control}
                    render={({ field }) => (
                      <FormControl
                        fullWidth
                        size="small"
                        error={
                          !!errors.containerVolumeUnits
                        }
                      >
                        <Select
                          {...field}
                          displayEmpty
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              borderTopLeftRadius: 0,
                              borderBottomLeftRadius: 0,
                            },
                            '& .MuiOutlinedInput-notchedOutline': {
                              borderLeft: 0,
                            },
                          }}
                        >
                          {volumeUnits.map((u) => (
                            <MenuItem key={u} value={u}>
                              {u}
                            </MenuItem>
                          ))}
                        </Select>

                        {errors.containerVolumeUnits && (
                          <FormHelperText>
                            {
                              errors.containerVolumeUnits
                                .message
                            }
                          </FormHelperText>
                        )}
                      </FormControl>
                    )}
                  />
                </Box>
              </Box>

              {/* Vendor */}
              <Box>
                <Typography
                  sx={{
                    fontSize: '14px',
                    color: '#555',
                    mb: '4px',
                  }}
                >
                  Vendor
                </Typography>

                <Controller
                  name="vendor"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      size="small"
                      fullWidth
                      placeholder="e.g. Eppendorf"
                    />
                  )}
                />
              </Box>
            </Box>

            {/* Same as Material */}
            <Controller
              name="sameAsMaterial"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  sx={{
                    ml: 0,
                    mt: '-2px',
                    mb: '10px',
                  }}
                  control={
                    <Checkbox
                      size="small"
                      checked={field.value}
                      onChange={(e) => {
                        field.onChange(e.target.checked);

                        if (e.target.checked) {
                          setValue(
                            'containerName',
                            materialName,
                          );
                        }
                      }}
                    />
                  }
                  label={
                    <Typography sx={{ fontSize: '14px' }}>
                      Same as Material
                    </Typography>
                  }
                />
              )}
            />
          </>
        ) : (
          <>
            {/* Well Plate Type */}
            <Box sx={{ mb: '10px' }}>
              <Typography
                sx={{
                  fontSize: '14px',
                  color: '#555',
                  mb: '4px',
                }}
              >
                Container Type
                <Box
                  component="span"
                  sx={{ color: '#dc3545', ml: '2px' }}
                >
                  *
                </Box>
              </Typography>

              <Controller
                name="containerType"
                control={control}
                render={({ field }) => (
                  <FormControl
                    fullWidth
                    size="small"
                    error={!!errors.containerType}
                  >
                    <Select
                      {...field}
                      displayEmpty
                      onChange={(e) => {
                        field.onChange(e);

                        if (e.target.value === 'Well Plate') {
                          setValue('sameAsMaterial', false);
                        }
                      }}
                    >
                      {CONTAINER_TYPES.map((t) => (
                        <MenuItem key={t} value={t}>
                          {t}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              />
            </Box>

            {/* Plate details */}
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns:
                  '1.2fr 0.8fr 0.8fr 0.8fr',
                gap: '12px',
                mb: '10px',
                alignItems: 'flex-end',
              }}
            >
              <Box>
                <Typography
                  sx={{
                    fontSize: '14px',
                    color: '#555',
                    mb: '4px',
                  }}
                >
                  Plate Name
                  <Box
                    component="span"
                    sx={{ color: '#dc3545', ml: '2px' }}
                  >
                    *
                  </Box>
                </Typography>

                <Controller
                  name="plateName"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      size="small"
                      fullWidth
                      placeholder="Name"
                      required
                      error={!!errors.plateName}
                      helperText={errors.plateName?.message}
                    />
                  )}
                />
              </Box>

              <Box>
                <Typography
                  sx={{
                    fontSize: '14px',
                    color: '#555',
                    mb: '4px',
                  }}
                >
                  Rows
                  <Box
                    component="span"
                    sx={{ color: '#dc3545', ml: '2px' }}
                  >
                    *
                  </Box>
                </Typography>

                <Controller
                  name="rows"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      type="number"
                      size="small"
                      fullWidth
                      placeholder="Rows"
                      error={!!errors.rows}
                      helperText={errors.rows?.message}
                      slotProps={{
                        htmlInput: {
                          min: 0,
                          max: 50,
                        },
                      }}
                    />
                  )}
                />
              </Box>

              <Box>
                <Typography
                  sx={{
                    fontSize: '14px',
                    color: '#555',
                    mb: '4px',
                  }}
                >
                  Columns
                  <Box
                    component="span"
                    sx={{ color: '#dc3545', ml: '2px' }}
                  >
                    *
                  </Box>
                </Typography>

                <Controller
                  name="columns"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      type="number"
                      size="small"
                      fullWidth
                      placeholder="Columns"
                      error={!!errors.columns}
                      helperText={errors.columns?.message}
                      slotProps={{
                        htmlInput: {
                          min: 0,
                          max: 50,
                        },
                      }}
                    />
                  )}
                />
              </Box>

              <Box>
                <Typography
                  sx={{
                    fontSize: '14px',
                    color: '#555',
                    mb: '4px',
                  }}
                >
                  Wells
                </Typography>

                <TextField
                  size="small"
                  fullWidth
                  value={rows * columns}
                  disabled
                />
              </Box>
            </Box>

            {/* Plate Volume */}
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: '26px',
              }}
            >
              <Box>
                <Typography
                  sx={{
                    fontSize: '14px',
                    color: '#555',
                    mb: '4px',
                  }}
                >
                  Volume
                </Typography>

                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 70px',
                  }}
                >
                  <Controller
                    name="wellVolumeAmount"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        type="number"
                        size="small"
                        fullWidth
                        placeholder="Value"
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderTopRightRadius: 0,
                            borderBottomRightRadius: 0,
                          },
                        }}
                      />
                    )}
                  />

                  <Controller
                    name="wellVolumeUnits"
                    control={control}
                    render={({ field }) => (
                      <FormControl fullWidth size="small">
                        <Select
                          {...field}
                          displayEmpty
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              borderTopLeftRadius: 0,
                              borderBottomLeftRadius: 0,
                            },
                            '& .MuiOutlinedInput-notchedOutline': {
                              borderLeft: 0,
                            },
                          }}
                        >
                          {WELL_PLATE_UNITS.map((u) => (
                            <MenuItem key={u} value={u}>
                              {u}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}
                  />
                </Box>
              </Box>

              {/* Vendor */}
              <Box>
                <Typography
                  sx={{
                    fontSize: '14px',
                    color: '#555',
                    mb: '4px',
                  }}
                >
                  Vendor
                </Typography>

                <Controller
                  name="vendor"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      size="small"
                      fullWidth
                      placeholder="e.g. Eppendorf"
                    />
                  )}
                />
              </Box>
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
}